import { useEffect } from 'react';

import { useAuth } from '@r-client/shared/data/auth';
import { isMobileWebView } from '@r-client/shared/data/client';

import { whenRumReady } from '../init-rum';
import { log } from '../log';
import { subscribeToAnalyticsConsent } from '../osano/subscribe-to-analytics-consent';
import { IRumUser } from '../types';

/**
 * Staff domains, lower-cased. `@republic.co` is the legacy domain longer-tenured
 * employees still use, and `@seedrs.com` came with the acquisition — both have
 * to be here, not just the current one.
 */
const INTERNAL_EMAIL_DOMAINS = [
  '@republic.com',
  '@republic.co',
  '@seedrs.com',
] as const;

const isInternalEmail = (email: string | null | undefined): boolean => {
  if (!email) {
    return false;
  }

  // Email domains are case-insensitive, and nothing upstream normalises them,
  // so `Jane@Seedrs.com` must match too.
  const normalized = email.toLowerCase().trim();

  return INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain));
};

/**
 * Attaches the viewer's id to the RUM session while Osano reports ANALYTICS
 * consent, and clears it otherwise. Id only; staff are never identified. See
 * ADR 20261015, "Privacy position".
 */
export const useRumUser = (): void => {
  const { viewer } = useAuth();

  const userId = viewer?.isAuthenticated ? viewer.info.id : undefined;
  const email = viewer?.isAuthenticated ? viewer.info.email : undefined;

  useEffect(() => {
    if (isMobileWebView()) {
      return;
    }

    let isCancelled = false;
    let unsubscribe: (() => void) | undefined;

    // The SDK loads in its own chunk, so wait for it rather than giving up.
    // Identity is attached whenever both the SDK and consent have arrived,
    // in either order.
    void whenRumReady().then((rum) => {
      if (!rum || isCancelled) {
        return;
      }

      let isConsentGranted = false;

      const applyIdentity = () => {
        if (!isConsentGranted || !userId || isInternalEmail(email)) {
          rum.clearUser();
          return;
        }

        // `satisfies` rejects any extra field (email, name), so the id-only rule
        // is enforced, not just stated.
        const user = { id: userId } satisfies IRumUser;

        log('setUser %s', userId);
        rum.setUser(user);
      };

      unsubscribe = subscribeToAnalyticsConsent((isGranted) => {
        isConsentGranted = isGranted;
        applyIdentity();
      });
    });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [userId, email]);
};
